/*
 *  -----------------------------------------------------------------------
 *    <copyright file="core-confirmation-dialog/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import Button from '../buttons';
import { useTranslation } from 'hooks';
import { CustomDialog, DialogActions, DialogContent, DialogTitle } from 'views/components/core/dialog';

import './styles.scss';

interface CoreConfirmationDialogInterface {
    close: () => void;
    open: boolean;
    onDecline?: () => void;
    onConfirm?: () => void;
    title: string;
    mainText: React.ReactNode;
    additionalText?: React.ReactNode;
    declineBtnText?: string;
    submitBtnText?: string;
    maxWidth?: string;
    hideButtons?: boolean;
}
const CoreConfirmationDialog = ({
    close,
    open,
    onDecline,
    onConfirm,
    title,
    mainText,
    additionalText,
    declineBtnText,
    submitBtnText,
    maxWidth = 'sm',
    hideButtons,
}: CoreConfirmationDialogInterface) => {
    const { t } = useTranslation();

    return (
        <CustomDialog
            onClose={close}
            fullWidth
            maxWidth={maxWidth}
            scroll="paper"
            open={open}
            className="custom-width-popup"
        >
            <DialogTitle id="confirmation-controls-dialog-modal-title">
                {title}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={close}
                    data-testid="close-dialog"
                />
            </DialogTitle>
            <DialogContent className="confirmation-dialog-content">
                <p className="main-text">{mainText}</p>
                <p className="main-text">{additionalText}</p>
                {!hideButtons && (
                    <DialogActions className="confirmation-actions">
                        <Button
                            id="no-confirm-control-btn"
                            className="confirmation-btn"
                            text={declineBtnText ?? t('common:no')}
                            onClick={onDecline}
                        />
                        <Button
                            id="confirm-control-btn"
                            className="confirmation-btn"
                            text={submitBtnText ?? t('common:yes')}
                            onClick={onConfirm}
                        />
                    </DialogActions>
                )}
            </DialogContent>
        </CustomDialog>
    );
};

export default CoreConfirmationDialog;
